import { useState, useRef } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';

const FileUpload = ({ currentUrl, onUpload, accept = "image/*,video/*", label = "Upload Image" }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    if (file.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(file));
    }

    setIsUploading(true);
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const fileUrl = `${API}/files/${res.data.id}`;
      onUpload(fileUrl);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const displayUrl = preview || currentUrl;
  const isVideo = currentUrl?.includes('.mp4') || currentUrl?.includes('.mov') || currentUrl?.includes('.webm');

  return (
    <div data-testid="file-upload" className="space-y-2">
      <p className="text-sm font-medium text-[#2C2C2C]">{label}</p>
      
      {displayUrl && !isVideo && (
        <div className="relative w-full h-40 rounded-xl overflow-hidden bg-[#F5F5F0] border border-[#E8DFD0]">
          <img src={displayUrl} alt="Preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => { setPreview(null); onUpload(''); }}
            className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {displayUrl && isVideo && (
        <div className="relative w-full h-40 rounded-xl overflow-hidden bg-[#F5F5F0] border border-[#E8DFD0]">
          <video src={displayUrl} className="w-full h-full object-cover" controls />
        </div>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-[#E8DFD0] hover:border-[#D4AF37] bg-[#F5F5F0] hover:bg-[#F5F0E0] text-[#6B7280] hover:text-[#D4AF37] transition-all duration-200"
      >
        {isUploading ? (
          <><Loader2 size={18} className="animate-spin" /> Uploading...</>
        ) : (
          <><Upload size={18} /> {displayUrl ? 'Change File' : label}</>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFile}
        className="hidden"
      />
    </div>
  );
};

export default FileUpload;
